import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import SieveFactorFields from '../components/SieveFactorFields';

interface ProcessType {
  value: number;
  label: string;
}

interface SieveFactor {
  id?: number;
  process_type_id: number;
  sieve_0_percentage?: string;
  sieve_12_percentage?: string;
  sieve_14_percentage?: string;
  sieve_15_percentage?: string;
  factor_14?: string;
  factor_15?: string;
  _destroy?: boolean;
}

interface SieveFactorsContainerProps {
  processTypes: ProcessType[];
  sieveFactors: SieveFactor[];
}

function SieveFactorsContainer({
  processTypes,
  sieveFactors,
}: SieveFactorsContainerProps) {
  const [, setSelectedProcessTypeIds] = useState<number[]>([]);
  const [currentSieveFactors, setCurrentSieveFactors] = useState<SieveFactor[]>(
    sieveFactors || []
  );

  const handleProcessTypeChange = () => {
    const hiddenInputs = document.querySelectorAll(
      'input[type="hidden"][name="fixed_price_contract[process_type_ids][]"]'
    );

    const newSelectedIds = Array.from(hiddenInputs)
      .map((input: any) => parseInt(input.value))
      .filter((id) => !isNaN(id));

    setSelectedProcessTypeIds(newSelectedIds);

    setCurrentSieveFactors((prevFactors) => {
      let updatedFactors = [...prevFactors];

      updatedFactors = updatedFactors
        .map((factor) => {
          if (!newSelectedIds.includes(factor.process_type_id)) {
            if (factor.id) {
              return { ...factor, _destroy: true };
            }
            return null;
          } else {
            return { ...factor, _destroy: false };
          }
        })
        .filter((factor) => factor !== null) as SieveFactor[];

      newSelectedIds.forEach((processTypeId) => {
        const existingFactor = updatedFactors.find(
          (f) => f.process_type_id === processTypeId
        );
        if (!existingFactor) {
          updatedFactors.push({
            process_type_id: processTypeId,
            _destroy: false,
          });
        }
      });

      return updatedFactors;
    });
  };

  useEffect(() => {
    handleProcessTypeChange();

    // Observe changes in the container where hidden inputs are rendered
    // The MultiSelect component renders hidden inputs dynamically
    const observer = new MutationObserver((mutations) => {
      // Check if any mutation involves process_type_ids hidden inputs
      const hasRelevantChange = mutations.some((mutation) => {
        return (
          Array.from(mutation.addedNodes).some((node: any) => {
            return (
              node.nodeName === 'INPUT' &&
              node.type === 'hidden' &&
              node.name === 'fixed_price_contract[process_type_ids][]'
            );
          }) ||
          Array.from(mutation.removedNodes).some((node: any) => {
            return (
              node.nodeName === 'INPUT' &&
              node.type === 'hidden' &&
              node.name === 'fixed_price_contract[process_type_ids][]'
            );
          })
        );
      });

      if (hasRelevantChange) {
        handleProcessTypeChange();
      }
    });

    // Observe the entire form for changes to hidden inputs
    const formElement = document.querySelector('form');
    if (formElement) {
      observer.observe(formElement, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Separate factors for display and hidden inputs
  const visibleFactors = currentSieveFactors.filter((factor) => !factor._destroy);
  const destroyedFactors = currentSieveFactors.filter(
    (factor) => factor._destroy && factor.id
  );

  return (
    <div>
      {visibleFactors.length === 0 && (
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          Select process types above to configure sieve factors.
        </p>
      )}

      {visibleFactors.map((sieveFactor, index) => {
        const processType = processTypes.find(
          (pt) => pt.value === sieveFactor.process_type_id
        );
        if (!processType) return null;

        return (
          <SieveFactorFields
            key={sieveFactor.id || `new-${sieveFactor.process_type_id}`}
            processTypeId={sieveFactor.process_type_id}
            processTypeName={processType.label}
            sieveFactor={sieveFactor}
            index={index}
          />
        );
      })}

      {/* Hidden inputs for destroyed persisted factors */}
      {destroyedFactors.map((sieveFactor, index) => {
        const attributeString = `fixed_price_contract[sieve_factors_attributes][${
          visibleFactors.length + index
        }]`;
        return (
          <div key={`destroy-${sieveFactor.id}`} style={{ display: 'none' }}>
            <input type='hidden' name={`${attributeString}[id]`} value={sieveFactor.id} />
            <input
              type='hidden'
              name={`${attributeString}[process_type_id]`}
              value={sieveFactor.process_type_id}
            />
            <input type='hidden' name={`${attributeString}[_destroy]`} value='1' />
          </div>
        );
      })}
    </div>
  );
}

export default observer(SieveFactorsContainer);
